import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * PacificClock — live clock locked to America/Los_Angeles.
 * 24-hour format with seconds. Updates every second.
 *
 * Props:
 *  - size: "xs" | "sm" | "md" | "lg"  (default "sm")
 *  - showLabel: boolean — show "PT" suffix (default true)
 *  - showDate: boolean — append day + date (default true)
 *  - className: extra wrapper classes
 *  - testId: data-testid (default "pacific-clock")
 */
export default function PacificClock({
  size = "sm",
  showLabel = true,
  showDate = true,
  className = "",
  testId = "pacific-clock",
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  const date = showDate
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        weekday: "short",
        month: "short",
        day: "numeric",
      }).format(now)
    : "";

  const sizes = {
    xs: { wrap: "text-[10px]", icon: 10, gap: "gap-1" },
    sm: { wrap: "text-xs", icon: 12, gap: "gap-1.5" },
    md: { wrap: "text-sm", icon: 14, gap: "gap-2" },
    lg: { wrap: "text-base", icon: 16, gap: "gap-2" },
  };
  const s = sizes[size] || sizes.sm;

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center ${s.gap} font-accent tracking-widest text-neutral-300 ${s.wrap} ${className}`}
      title="Live Pacific Time"
    >
      <Clock size={s.icon} className="text-[#D4AF37] flex-shrink-0" />
      <span className="font-mono tabular-nums text-[#D4AF37] font-bold">
        {time}
      </span>
      {showLabel && (
        <span className="text-neutral-500 uppercase">PT</span>
      )}
      {showDate && date && (
        <>
          <span className="text-neutral-700">·</span>
          <span className="uppercase">{date}</span>
        </>
      )}
    </span>
  );
}
